import { ArrowBackIosNew, Close } from '@mui/icons-material';
import { Drawer, IconButton, ThemeProvider, Typography, createTheme, useTheme } from '@mui/material';
import { merge } from 'lodash-es';
import { useEffect, useState } from 'react';
import { Provider as AncestorsProvider, useIsAncestor } from '#contexts/Ancestors/index.ts';
import { Ancestor } from '#enums/ancestors.ts';
import { Loader } from '../Loader/index.tsx';
import { Container, DrawerHeader } from './styled.ts';
import { themeOverrides } from './themeOverrides.ts';
import { type SlideoutDrawerProps } from './types.ts';

export const SlideoutDrawer = ({
  ancestor,
  anchor = 'right',
  children,
  containerSx,
  headerIcon,
  loading = false,
  open = false,
  setOpen,
  sx = {},
  title,
}: SlideoutDrawerProps) => {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const theme = useTheme();
  const isAncestorSlideoutDrawer = useIsAncestor(Ancestor.ListingPreview);

  useEffect(() => {
    if (internalOpen !== open) {
      setInternalOpen(open);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Drawer
      anchor={anchor}
      onClose={() => {
        setOpen(false);
      }}
      open={internalOpen}
      sx={merge(
        {
          '.MuiDrawer-paper': {
            backgroundColor: theme.palette.grey[900],
            maxWidth: '100%',
            width: '100%',
            [theme.breakpoints.up('md')]: {
              width: '75%',
            },
            [theme.breakpoints.up('xl')]: {
              width: '50%',
            },
          },
        },
        sx,
      )}
      transitionDuration={300}
    >
      <DrawerHeader anchor={anchor}>
        <IconButton
          aria-label="close"
          onClick={() => {
            setOpen(false);
          }}
          sx={{
            color: theme.palette.grey[100],
          }}
        >
          {headerIcon !== 'close' && isAncestorSlideoutDrawer ? <ArrowBackIosNew /> : <Close />}
        </IconButton>
        {title ? (
          <Typography
            sx={{
              color: theme.palette.grey[100],
              marginLeft: theme.spacing(1),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            variant="h6"
          >
            {title}
          </Typography>
        ) : undefined}
      </DrawerHeader>
      {loading ? (
        <Loader sx={{ marginTop: theme.spacing(6) }} />
      ) : (
        <AncestorsProvider ancestor={ancestor}>
          <ThemeProvider theme={createTheme({ ...theme, ...themeOverrides })}>
            <Container sx={containerSx}>{children}</Container>
          </ThemeProvider>
        </AncestorsProvider>
      )}
    </Drawer>
  );
};
